import { Currency, Ether, NativeCurrency, Token, WETH9 } from '@x-swap-protocol/sdk-core'
import { SupportedChainId } from 'constants/chains'
import invariant from 'tiny-invariant'

import { UNI_ADDRESS } from './addresses'

export const NATIVE_CHAIN_ID = 'XDC'

export const DEFAULT_ERC20_DECIMALS = 18

export const XSP_XDC = new Token(
  SupportedChainId.XDC,
  '0x36726235dAdbdb4658D33E62a249dCA7c4B2bC68',
  18,
  'XSP',
  'XSwapProtocol'
)

export const XTT_XDC = new Token(
  SupportedChainId.XDC,
  '0x17476dc3eda45aD916cEAdDeA325B240A7FB259D',
  18,
  'XTT',
  'XSwap Treasury Token'
)

export const NATIVE_USDC_XDC = new Token(
  SupportedChainId.XDC,
  '0xfa2958cb79b0491cc627c1557f441ef849ca8eb1',
  6,
  'USDC',
  'USDC'
)

export const USDC_XDC = new Token(
  SupportedChainId.XDC,
  '0x2a8e898b6242355c290e1f4fc966b8788729a4d4',
  6,
  'USDC.e',
  'Bridged USDC(XDC)'
)

export const USDC_APOTHEM = new Token(
  SupportedChainId.XDC_TESTNET,
  '0x7a619b7667ef680266f3c5eba5c98b9c63cec0ba',
  18,
  'MP7',
  'MaiPhuong7'
)

export const WBTC = new Token(
  SupportedChainId.MAINNET,
  '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
  8,
  'WBTC',
  'Wrapped BTC'
)

export const renBTC = new Token(
  SupportedChainId.MAINNET,
  '0xEB4C2781e4ebA804CE9a9803C67d0893436bB27D',
  8,
  'renBTC',
  'renBTC'
)

export const UNI: { [chainId: number]: Token } = {}

export const XDC: { [chainId: number]: Token } = {
  [SupportedChainId.XDC]: new Token(SupportedChainId.XDC, UNI_ADDRESS[50], 18, 'XDC', 'Xswap'),
  [SupportedChainId.XDC_TESTNET]: new Token(SupportedChainId.XDC_TESTNET, UNI_ADDRESS[51], 18, 'XDC', 'Xswap'),
}

export const WRAPPED_NATIVE_CURRENCY: { [chainId: number]: Token | undefined } = {
  ...(WETH9 as Record<SupportedChainId, Token>),
  [SupportedChainId.OPTIMISM]: new Token(
    SupportedChainId.OPTIMISM,
    '0x4200000000000000000000000000000000000006',
    18,
    'WETH',
    'Wrapped Ether'
  ),
  [SupportedChainId.OPTIMISM_GOERLI]: new Token(
    SupportedChainId.OPTIMISM_GOERLI,
    '0x4200000000000000000000000000000000000006',
    18,
    'WETH',
    'Wrapped Ether'
  ),
  [SupportedChainId.ARBITRUM_ONE]: new Token(
    SupportedChainId.ARBITRUM_ONE,
    '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1',
    18,
    'WETH',
    'Wrapped Ether'
  ),
  [SupportedChainId.ARBITRUM_GOERLI]: new Token(
    SupportedChainId.ARBITRUM_GOERLI,
    '0xe39Ab88f8A4777030A534146A9Ca3B52bd5D43A3',
    18,
    'WETH',
    'Wrapped Ether'
  ),
  [SupportedChainId.POLYGON]: new Token(
    SupportedChainId.POLYGON,
    '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270',
    18,
    'WMATIC',
    'Wrapped MATIC'
  ),
  [SupportedChainId.POLYGON_MUMBAI]: new Token(
    SupportedChainId.POLYGON_MUMBAI,
    '0x9c3C9283D3e44854697Cd22D3Faa240Cfb032889',
    18,
    'WMATIC',
    'Wrapped MATIC'
  ),
  [SupportedChainId.CELO]: new Token(
    SupportedChainId.CELO,
    '0x471ece3750da237f93b8e339c536989b8978a438',
    18,
    'CELO',
    'Celo native asset'
  ),
  [SupportedChainId.CELO_ALFAJORES]: new Token(
    SupportedChainId.CELO_ALFAJORES,
    '0xf194afdf50b03e69bd7d057c1aa9e10c9954e4c9',
    18,
    'CELO',
    'Celo native asset'
  ),
  [SupportedChainId.BNB]: new Token(
    SupportedChainId.BNB,
    '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
    18,
    'WBNB',
    'Wrapped BNB'
  ),
  [SupportedChainId.XDC]: new Token(
    SupportedChainId.XDC,
    '0x951857744785E80e2De051c32EE7b25f9c458C42',
    18,
    'WXDC',
    'WXDC'
  ),
  [SupportedChainId.XDC_TESTNET]: new Token(
    SupportedChainId.XDC_TESTNET,
    '0x2a5c77b016Df1b3b0AE4E79a68F8adF64Ee741ba',
    18,
    'WXDC',
    'WXDC'
  ),
}

export function isCelo(chainId: number): chainId is SupportedChainId.CELO | SupportedChainId.CELO_ALFAJORES {
  return chainId === SupportedChainId.CELO_ALFAJORES || chainId === SupportedChainId.CELO
}

function isXdc(chainId: number): chainId is SupportedChainId.XDC | SupportedChainId.XDC_TESTNET {
  return chainId === SupportedChainId.XDC || chainId === SupportedChainId.XDC_TESTNET
}

class XdcNativeCurrency extends NativeCurrency {
  equals(other: Currency): boolean {
    return other.isNative && other.chainId === this.chainId
  }

  get wrapped(): Token {
    if (!isXdc(this.chainId)) throw new Error('Not bnb')
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    invariant(wrapped instanceof Token)
    return wrapped
  }

  public constructor(chainId: number) {
    if (!isXdc(chainId)) throw new Error('Not xdc')
    super(chainId, 18, 'XDC', 'XDC')
  }
}

class ExtendedEther extends Ether {
  public get wrapped(): Token {
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    if (wrapped) return wrapped
    throw new Error(`Unsupported chain ID: ${this.chainId}`)
  }

  private static _cachedExtendedEther: { [chainId: number]: NativeCurrency } = {}

  public static onChain(chainId: number): ExtendedEther {
    return this._cachedExtendedEther[chainId] ?? (this._cachedExtendedEther[chainId] = new ExtendedEther(chainId))
  }
}

const cachedNativeCurrency: { [chainId: number]: NativeCurrency | Token } = {}
export function nativeOnChain(chainId: number): NativeCurrency | Token {
  if (cachedNativeCurrency[chainId]) return cachedNativeCurrency[chainId]
  let nativeCurrency: NativeCurrency | Token
  if (isXdc(chainId)) {
    nativeCurrency = new XdcNativeCurrency(chainId)
  } else {
    nativeCurrency = ExtendedEther.onChain(chainId)
  }
  return (cachedNativeCurrency[chainId] = nativeCurrency)
}

export function getSwapCurrencyId(currency: Currency): string {
  if (currency.isToken) {
    return currency.address
  }
  return NATIVE_CHAIN_ID
}

export const TOKEN_SHORTHANDS: { [shorthand: string]: { [chainId in SupportedChainId]?: string } } = {
  USDC: {
    [SupportedChainId.XDC]: NATIVE_USDC_XDC.address,
    [SupportedChainId.XDC_TESTNET]: USDC_APOTHEM.address,
  },
}
